import React from 'react'
import { useSelector } from 'react-redux'

import { Account, BudgetFrequency } from 'api/interfaces'
import { useCampaignBuilderActivateMutation } from 'api/rtkQueryApi/platform/campaignBuilderApi'
import { useUpdateAudienceFootprintMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { useCreateDisplayAdsMutation } from 'api/rtkQueryApi/platform/creativesApi'
import { useGetAudienceFootprintSupportQuery } from 'api/rtkQueryApi/platform/organizationsApi'
import { RootState } from 'app/rootReducer'
import { AdPaymentType } from 'features/builders/internet/retail/builderInternetRetailSlice'

import { hasFootprint } from './FootprintSelector'
import { QuickCampaignChannel, QuickCampaignChannels, QuickCampaignResult, QuickCampaignResultStatus, QuickCampaignShared, campaignNameFor, selectedChannelsOf } from './quickCampaignModels'
import { buildInternetCampaignWorkflow, buildRemainingDisplayAds, buildTvCampaignWorkflow } from './quickCampaignWorkflows'

export interface QuickCampaignSubmitState {
  submit: () => Promise<QuickCampaignResult[]>
  isSubmitting: boolean
  results: QuickCampaignResult[]
}

const defaultFailureMessage = 'Campaign could not be created'
const footprintWarning = 'Footprint could not be saved'
const additionalAdsWarning = 'Additional display ads could not be saved'

const describeError = (error: unknown): string => {
  if (error instanceof Error && error.message.length > 0) {
    return error.message
  }
  const data = typeof error === 'object' && error !== null && 'data' in error ? (error as { data: unknown }).data : undefined
  if (typeof data === 'string' && data.length > 0) {
    return data
  }
  if (typeof data === 'object' && data !== null && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
    return (data as { message: string }).message
  }
  return defaultFailureMessage
}

const failedResultOf = (channel: QuickCampaignChannel, campaignName: string, message: string): QuickCampaignResult => ({ channel, campaignName, status: QuickCampaignResultStatus.Failed, message })

const resultStatusOf = (warnings: string[]) => (warnings.length > 0 ? QuickCampaignResultStatus.CreatedWithWarnings : QuickCampaignResultStatus.Created)

export const useQuickCampaignSubmit = (shared: QuickCampaignShared, channels: QuickCampaignChannels): QuickCampaignSubmitState => {
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [campaignBuilderActivate] = useCampaignBuilderActivateMutation()
  const [createDisplayAds] = useCreateDisplayAdsMutation()
  const [updateAudienceFootprint] = useUpdateAudienceFootprintMutation()
  const audienceFootprintSupportQuery = useGetAudienceFootprintSupportQuery()
  const audienceFootprintSupported = audienceFootprintSupportQuery.data?.isSupported ?? false
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [results, setResults] = React.useState<QuickCampaignResult[]>([])

  const createCampaign = async (account: Account, channel: QuickCampaignChannel, campaignName: string): Promise<QuickCampaignResult> => {
    const isInternet = channel === QuickCampaignChannel.Internet

    const activation = await campaignBuilderActivate({
      accountId: account.id,
      workflow: isInternet ? buildInternetCampaignWorkflow(shared, campaignName, channels.internet) : buildTvCampaignWorkflow(shared, campaignName, channels.tv),
      purchaseDetails: {
        paymentMethodType: AdPaymentType.Standard,
        paymentMethodId: '',
        budgetFrequency: BudgetFrequency.Total
      },
      isSaveForLater: true
    })

    if ('error' in activation) {
      return failedResultOf(channel, campaignName, describeError(activation.error))
    }

    const campaignId = activation.data.campaignId
    if (!campaignId) {
      return failedResultOf(channel, campaignName, defaultFailureMessage)
    }

    const warnings: string[] = []

    if (isInternet) {
      const bulkRequest = buildRemainingDisplayAds(channels.internet, account, campaignId)
      if (bulkRequest.displayAds.length > 0) {
        const bulkResult = await createDisplayAds(bulkRequest)
        if ('error' in bulkResult) {
          warnings.push(additionalAdsWarning)
        }
      }
    }

    if (audienceFootprintSupported && hasFootprint(shared.footprint)) {
      const footprintResult = await updateAudienceFootprint({ accountId: account.id, campaignId, footprint: shared.footprint })
      if ('error' in footprintResult) {
        warnings.push(footprintWarning)
      }
    }

    return { channel, campaignName, campaignId, status: resultStatusOf(warnings), message: warnings.length > 0 ? warnings.join('. ') : undefined }
  }

  const runCampaign = async (account: Account, channel: QuickCampaignChannel): Promise<QuickCampaignResult> => {
    const campaignName = campaignNameFor(shared.campaignName, channel, channels)
    try {
      return await createCampaign(account, channel, campaignName)
    } catch (error) {
      return failedResultOf(channel, campaignName, describeError(error))
    }
  }

  const submit = async (): Promise<QuickCampaignResult[]> => {
    const account = currentAccount!
    setIsSubmitting(true)
    const collectedResults: QuickCampaignResult[] = []
    try {
      for (const channel of selectedChannelsOf(channels)) {
        collectedResults.push(await runCampaign(account, channel))
      }
    } finally {
      setResults(collectedResults)
      setIsSubmitting(false)
    }
    return collectedResults
  }

  return { submit, isSubmitting, results }
}
